import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import taxService from '@/services/billing/taxService';
import expenseService from '@/services/billing/expenses/expenseService';
import matterService from '@/services/matter/matterService';
import userService from '@/services/setting/userService';
import { useAbility } from '@/providers/';
import { toast } from 'sonner';
import { KeenIcon, DefaultTooltip } from '@/components';
import { Can } from '@casl/react';
import { ItemsModal } from '@/pages/billing';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { useAuthContext } from '@/auth';
import { Link, useNavigate } from 'react-router-dom';
import { renderMultiAvatars, FiltersToFormData, getAccessToken, decodeHtmlEntities } from '@/utils'
import { createDataTableOptions, createDataTableConfig } from '@/utils';
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal, useContactLookup } from '@/hooks';
import { SelectInput, DateInput, STATUS_INVOICE_LISTING_OPTIONS, FilterContainer } from '@/components/TableFilter'
import { cn } from '@/lib/utils';
import invoiceService from '@/services/billing/invoice/invoiceService';
import bulkDeleteService from '@/services/bulk-delete/bulkDeleteService';
import { goTo403 } from "@/utils/Navigation";
import { Send, MousePointerClick, MousePointer2, BadgeDollarSign, ChevronDown } from 'lucide-react'
import { PaymentModal } from '@/components/modal';
import { ColumnToggleDropdown } from '../TableFilter/ColumnToggleDropdown';
import { SettlementEditorModal } from '../invoice';
import { useUserOptions } from '@/hooks'
import useDataTableBulkDelete from '@/hooks/useDataTableBulkDelete';
import { BulkDeleteActions } from '@/components/BulkDeleteActions';

const INVOICE_BADGE_CLASSES = {
  billed: 'badge-success',
  approved: 'badge-success',
  paid: 'badge-success',
  pending: 'badge-warning',
};

const PAYMENT_STATUS_CLASSES = {
  pending: 'badge-warning',
  paid: 'badge-success',
  partially_paid: 'badge-info',
};

DataTable.use(DT);
const InvoiceContactTab = ({ contactID, matterID, contactData, fetchPaymentHistory, fetchActivity, activityPerm }) => {
  if (!contactData || Object.keys(contactData).length === 0) {
    return null; // or loading UI
  }
  const { logout } = useAuthContext();
  const fetchContactLookup = useContactLookup();
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [selectedData, setSelectedData] = useState(null);
  const [itemId, setItemId] = useState(null)
  const { ability } = useAbility();
  const [showFilter, setShowFilter] = useState(false);
  const [filterData, setFilterData] = useState({
    approved_by: null,
    invoice_status: null,
    payment_status: null,
    contact_id: contactID,
    matter_id: null,
    from_date: null,
    to_date: null,
    date_range: null,
  });
  const API_URL = import.meta.env.VITE_APP_BASE_URL;
  const token = getAccessToken();
  const tableRef = useRef(null);
  const filterRef = useRef(filterData);
  const canDeleteInvoice = ability.can('delete', 'invoice');
  const canEditInvoice = ability.can('handle', 'invoice');
  const canApproveInvoice = ability.can('approve', 'invoice');
  const Navigate = useNavigate()
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();
  const [invoicePaymentHistory, setInvoicePaymentHistory] = useState(0.00)
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false)
  // Offline payment States
  const [offlinePaymentModal, setOfflinePaymentModal] = useState(false)
  const [offlinePaymentLoading, setOfflinePaymentLoading] = useState(false)
  const [paymentRows, setPaymentRows] = useState([]);
  const [hasPayable, setHasPayable] = useState(false);
  const [isMatterAvailable, setIsMatterAvailable] = useState(false)
  const [selectionMode, setSelectionMode] = useState('payment');
  const [allSelectedPayable, setAllSelectedPayable] = useState(false);
  const selectionModeRef = useRef('payment');
  const loadUserOptions = useUserOptions({ defaultPageSize: 20 });
  // const [paymentRows, setPaymentRows] = useState([]);


  const refresh = useCallback(() => {
    const table = tableRef.current?.dt?.();
    if (!table) return;
    table.ajax.reload();
  }, []);

  const fetchInvoicePaymentHistory = useCallback(async () => {
    setPaymentHistoryLoading(true)
    try {
      const { data, status } = await invoiceService.getInvoicePaymentDetails();
      if (status) {
        console.log('data', data)
        setInvoicePaymentHistory(data)
      }
    } catch (error) {
      console.error('Error', error)
      toast.error('Failed to fetch Invoice Payment History.', {
        description: 'An error occurred while fetching invoice payment history. Please try again later.',

      });
    } finally {
      setPaymentHistoryLoading(false)
    }
  }, []);
  const {
    selectedRows,
    allSelected,
    bulkDeleteLoading,
    bulkDeleteOpen,
    openBulkDelete,
    closeBulkDelete,
    confirmBulkDelete,
    toggleSelectPage,
  } = useDataTableBulkDelete({
    tableRef,
    model: 'Invoice',
    deleteFn: bulkDeleteService.bulkDelete,
    onSuccess: () => {
      refresh();
      fetchPaymentHistory();
      if (activityPerm) fetchActivity();
      toast.success('Invoices Deleted', {
        description: 'Selected invoices deleted successfully.',
      });
    },
    onError: (error) => {
      console.error('Error', error)
      if (error?.message == "Can't delete: Matter records are related to this Invoice.") {
        toast.warning('Failed to delete Invoice.', {
          description: `Can't delete: Matter records are related to this Invoice.`,
        });
        return;
      }
      toast.error('Failed to delete Invoice.', {
        description: 'An error occurred while deleting invoices. Please try again later.',
      });
    },
  });


  // 1) Helpers – put these above `options`
  const isOpenMatter = (matter) =>
    !!matter && matter.status !== 'closed' && matter.status !== 'archived';

  // const isPayable = (row) =>
  // (
  //   row.invoice_status === 'approved' ||
  //   (row.invoice_status === 'billed' && row.payment_status === 'pending')
  // );



  useEffect(() => {
    filterRef.current = filterData;
  }, [filterData]);

  // Imported invoices are settled like any other (OL-145).
  const isPayable = useCallback((row) => (
    row?.payment_status !== 'partially_paid' && (
      row.invoice_status === 'approved' ||
      (row.invoice_status === 'billed' && row.payment_status === 'pending')
    )
  ), []);

  const headerCheckboxId = 'invoice-contact-table-select-all';

  const syncHeaderCheckbox = useCallback(() => {
    const table = tableRef?.current?.dt?.();
    if (!table) return;
    const headerCheckbox = document.getElementById(headerCheckboxId);
    if (!headerCheckbox) return;
    const totalOnPage = selectionModeRef.current === 'payment'
      ? table.rows((idx, data) => isPayable(data)).count()
      : table.rows({ page: 'current' }).count();
    const selectedOnPage = selectionModeRef.current === 'payment'
      ? table.rows({ selected: true }).data().toArray().filter(isPayable).length
      : table.rows({ selected: true, page: 'current' }).count();
    headerCheckbox.checked = totalOnPage > 0 && totalOnPage === selectedOnPage;
    headerCheckbox.indeterminate = selectedOnPage > 0 && selectedOnPage < totalOnPage;
  }, [isPayable, tableRef]);

  // Reflect each row's selection state on its (display-only) checkbox input.
  const syncRowCheckboxes = useCallback(() => {
    const table = tableRef?.current?.dt?.();
    if (!table) return;
    table.rows({ page: 'current' }).every(function () {
      const node = this.node();
      if (!node) return;
      const input = node.querySelector('.row-select-checkbox');
      if (input) input.checked = node.classList.contains('selected');
    });
  }, [tableRef]);

  const setupHeaderCheckbox = useCallback((api) => {
    const headerCell = api.column('checkbox:name').header();
    if (!headerCell) return;
    headerCell.innerHTML = `
      <label class="inline-flex items-center cursor-pointer">
        <input id="${headerCheckboxId}" type="checkbox" class="checkbox checkbox-sm cursor-pointer form-checkbox" />
      </label>
    `;
    const headerCheckbox = headerCell.querySelector(`#${headerCheckboxId}`);
    if (headerCheckbox) {
      headerCheckbox.addEventListener('change', (event) => {
        event.stopPropagation();
        const table = tableRef?.current?.dt?.();
        if (!table) return;
        if (event.target.checked) {
          if (selectionModeRef.current === 'payment') {
            table.rows((idx, data) => isPayable(data)).select();
          } else {
            table.rows({ page: 'current' }).select();
          }
        } else {
          table.rows({ page: 'current' }).deselect();
        }
        syncHeaderCheckbox();
      });
    }
  }, [isPayable, syncHeaderCheckbox, tableRef]);

  const columns = useMemo(() => ([
    // {
    //   title: 'S#',
    //   name: 'serial',
    //   data: null,
    //orderable: true,se,
    //   render: (data, type, row, meta) => meta.row + 1,
    // },
    {
      data: null,
      defaultContent: '',
      className: 'dt-row-select cursor-pointer text-center',
      id: 'invoice-checkbox-payable',
      orderable: false,
      name: 'checkbox',
      render: function () {
        // Real styled checkbox so row selection matches the header "select all"
        // checkbox. pointer-events-none lets the click fall through to the cell,
        // which DataTables Select uses to toggle selection (see select.selector).
        return '<input type="checkbox" tabindex="-1" aria-label="Select row" class="checkbox checkbox-sm form-checkbox row-select-checkbox pointer-events-none" />';
      },
    },
    {
      data: 'number',
      title: 'Number',
      name: 'number',
      orderable: true,
      render: function (data, type, row) {
        // return `<div>INVOICE-${row.number}</div>`;
        return `<div id="number-cell-${row.id}"></div>`;
      }
    },
    {
      data: 'is_import',
      title: 'Imported Invoice',
      name: 'is_import',
      render: function (value) {
        console.log(value)
        const is_import = value === 1 ? 'Imported' : '-';
        if (value === 0) return is_import;
        return `<span class="capitalize badge badge-pill badge-outline badge-info gap-1 items-center">
        <span class="badge badge-dot size-1.5 badge-info"></span>
        ${is_import}
      </span>`;
      }
    },
    { data: 'date', title: 'Date', name: 'date' },
    {
      data: 'invoice_status',
      title: 'Invoice Status',
      name: 'invoice_status',
      render: function (value) {
        if (!value) return '-';
        return `<span class="badge badge-pill badge-outline capitalize ${INVOICE_BADGE_CLASSES[value]}">${value}</span>`
      }
    },
    {
      data: 'payment_status',
      title: 'Payment Status',
      name: 'payment_status',
      render: function (value) {
        if (!value) return '-';
        return `<span class="badge badge-pill badge-outline capitalize ${PAYMENT_STATUS_CLASSES[value]}">${value.split('_').join(' ')}</span>`
      }
    },
    {
      data: 'payment_note',
      title: 'Payment Note',
      name: 'payment_note',
      render: function (data, type, row) {
        return `<div>${data || '-'}</div>`;
      }
    },
    {
      data: 'approved_by',
      title: 'Approved By',
      name: 'approved_by',
      orderable: true,
      render: function (data, type, row) {
        return `<div id="user-cell-${row.id}"></div>`;
      }
    },
    {
      data: 'total',
      title: 'Total',
      name: 'total',
      render: function (data, type, row) {
        return `<div>$${row.total}</div>`;
      }
    },
    {
      data: 'partial_payment_amount',
      title: 'Paid Amount',
      name: 'partial_payment_amount',
      render: function (data, type, row) {
        if (row.partial_payment_amount === null) return '-';
        return `<div>$${row.partial_payment_amount}</div>`;
      }
    },
    {
      data: 'settlements',
      title: 'Settlement Amount',
      name: 'settlements',
      orderable: true,
      render: function (data, type, row) {
        return `<div id="settlement-cell-${row.id}"></div>`;
      },
    },
    {
      data: 'contact',
      title: 'Contact',
      name: 'contact',
      orderable: true,
      render: function (data, type, row) {
        return `<div id="contact-cell-${row.id}"></div>`;
      }
    },
    {
      data: 'matter',
      title: 'Matter',
      name: 'matter',
      orderable: true,
      render: function (data, type, row) {
        return `<div id="matter-cell-${row.id}"></div>`;
      }
    },
    {
      data: 'invoice_age',
      title: 'Aging',
      name: 'invoice_age',
      render: function (data, type, row) {
        if (row.invoice_age === null) return '-';
        return `<div>${row.invoice_age}</div>`;
      }
    },
    {
      orderable: true, title: 'Created At', name: 'created_at', data: 'created_at',
      defaultContent: '-'
    },
    {
      title: 'Actions',
      name: 'actions',
      data: null,
      orderable: false,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="action-cell-${row.id}"></div>`;
      }
    }
  ]), []);
  const defaultVisibleColumns = [
    // 'serial',
    'checkbox',
    'number',
    'is_import',
    'date',
    'invoice_status',
    'payment_status',
    'payment_note',
    'approved_by',
    'total',
    'partial_payment_amount',
    'settlements',
    'contact',
    'matter',
    'invoice_age',
    'created_at',
    'actions'
  ];

  const handleDelete = async () => {
    closeModal();
    try {
      const { status } = await invoiceService.deleteInvoice(actionId);
      if (status) {
        refresh()
        fetchPaymentHistory()
        if (activityPerm) fetchActivity()
        toast.success('Deleted Invoice.', {
          description: 'Invoice deleted Successfully.',
        });
      }
    } catch (error) {
      console.error('Error', error)
      if (error.message == "Can't delete: Matter records are related to this Invoice.") {
        toast.warning('Failed to delete Expense.', {
          description: `Can't delete: Matter records are related to this Invoice.`,
        });
        return
      }
      toast.error('Failed to delete Invoice.', {
        description: 'An error occurred while deleting invoice. Please try again later.',
      });
    }
  }

  const handleEdit = useCallback((id) => {
    Navigate(`/billing/invoice/edit/${id}/`, {
      state: {
        from: `/contacts/${contactID}`,
        // from: `/contacts/${contactID}?tab=Invoices`,
        contactId: contactID,
        matterId: matterID
      }
    })
  }, [Navigate, contactID, matterID]);

  const handleClone = useCallback((id) => {
    Navigate(`/billing/invoice/create/${id}/`, {
      state: {
        from: `/contacts/${contactID}`,
        // from: `/contacts/${contactID}?tab=Invoices`,
        contactId: contactID,
        matterId: matterID
      }
    })
  }, [Navigate, contactID, matterID]);

  const handleApprove = useCallback(async (id) => {
    try {
      setLoading(true);
      const { data, status } = await invoiceService.aprroveInvoice(id)
      if (status) {
        toast.success('Approved Successfully.', {
          description: 'Invoice Approved Successfully.',

        });
        refresh()
        fetchInvoicePaymentHistory()
      }
    } catch (error) {
      console.error('Error:', error)
      toast.error('Failed to approve Invoice.', {
        description: 'An error occurred while approving invoice. Please try again later.',

      });
    } finally {
      setLoading(false)
    }
  }, [fetchInvoicePaymentHistory, refresh]);

  const handleModalClose = () => {
    setItemId(null)
    setSelectedData(null)
    setMode('create')
    setOpen(false)
  }

  const handleSendInvoicetoContact = useCallback(async (id) => {
    try {
      const { status } = await invoiceService.sendInvoiceToContact(id)
      if (status) {
        toast.success('Invoice Sent Successfully.', {
          description: 'Invoice has been sent to contact successfully.',
        })
        refresh()
        fetchInvoicePaymentHistory()
      }
    } catch (error) {
      console.error('Error', error)
      toast.error('Failed to send invoice to contact.', {
        description: 'An error occurred while sending invoice to contact. Please try again later.',
      })
    }
  }, [fetchInvoicePaymentHistory, refresh]);

  const handleNavigateConatct = useCallback((id) => {
    Navigate(`/contacts/${id}/`)
  }, [Navigate]);

  const handlePreviewInvoice = useCallback((id) => {
    Navigate(`/billing/invoice/preview/${id}`);
  }, [Navigate]);

  const handleNavigateMatter = useCallback((id) => {
    Navigate(`/matters/detail/${id}/`)
  }, [Navigate]);

  const renderNumber = useCallback((row) => {
    const fullNumber = `INVOICE-${row.number}`;
    const { id, is_import } = row;
    return (
      <span onClick={() => { handlePreviewInvoice(id) }} className='cursor-pointer link'>{fullNumber}</span>
    )
  }, [handlePreviewInvoice]);

  const renderContactName = useCallback((row) => {
    if (!row.contact) {
      return <span>-</span>;
    }
    const fullName = row.contact.first_name + ' ' + row.contact.last_name;
    const { id } = row.contact;
    return (
      <span onClick={() => { handleNavigateConatct(id) }} className='cursor-pointer link'>{fullName}</span>
    )
  }, [handleNavigateConatct]);

  const renderMatterName = useCallback((row) => {
    const { matter } = row;
    const fullName = decodeHtmlEntities(matter?.name || '');

    return (
      <span
        onClick={() => handleNavigateMatter(matter?.id || '')}
        className="cursor-pointer link"
      >
        {fullName}
      </span>
    );
  }, [handleNavigateMatter]);

  const renderCustomActions = useCallback((row) => {
    const { id, payment_status, invoice_status, is_approval_required, matter, contact, is_import } = row;
    console.log('asdasdasd', contact)

    // Imported invoices have no line items: they can be paid, sent, approved or deleted, not edited or cloned (OL-145).
    const isImported = is_import === 1;
    // const isEditable = payment_status === 'pending' && (invoice_status === 'pending' || invoice_status === 'approved' || invoice_status === 'paid'); // only editable when invoice is not approved
    const isEditable =
      (canEditInvoice) ||
      (canApproveInvoice && invoice_status === "approved");
    const matterAvialable = matter?.status !== 'closed' && matter?.status !== 'archived'
    const showPaymentButtons =
      canEditInvoice &&
      (invoice_status === 'approved' ||
        (invoice_status === 'billed' && payment_status !== 'paid'));
    return (
      <DropdownMenu>
        {canEditInvoice && contactData && contactData.status === 'active' && (
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-gray-600">
              <KeenIcon icon="dots-vertical" className="text-[18px]" />
            </Button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent align="end" className="w-40">
          {showPaymentButtons && (
            <DropdownMenuItem onClick={() => {
              setPaymentRows([row]);
              setOfflinePaymentModal(true);
            }}>
              <KeenIcon icon="bill" className="mr-0.5 h-4 w-4 cursor-pointer" /> Pay Invoice
            </DropdownMenuItem>
          )}
          {/* {showPaymentButtons && ( */}
          {canEditInvoice && invoice_status !== 'paid' && payment_status !== 'paid' && !(invoice_status === 'pending' && Number(is_approval_required) === 1) && (
            <DropdownMenuItem onClick={() => {
              if (!contact?.email?.trim()) {
                toast.warning('Missing Email Address', {
                  description: 'This contact has no email. Please add one before proceeding.',
                });
                return
              }
              handleSendInvoicetoContact(id)
            }}>
              <Send className="mr-0.5 h-4 w-4 cursor-pointer" /> Send Invoice
            </DropdownMenuItem>
          )}
          {canApproveInvoice && invoice_status === 'pending' && payment_status !== 'paid' && is_approval_required === 1 && (
            <DropdownMenuItem onClick={() => handleApprove(id)}>
              <KeenIcon icon="archive-tick" className="mr-0.5 h-4 w-4 cursor-pointer" /> Approve
            </DropdownMenuItem>
          )}
          {canEditInvoice && !isImported && (
            <DropdownMenuItem onClick={() => handleClone(id)}>
              <KeenIcon icon="note-2" className="mr-0.5 h-3.5 w-4 cursor-pointer" /> Clone
            </DropdownMenuItem>
          )}
          {isEditable && !isImported && (
            <DropdownMenuItem onClick={() => handleEdit(id)}>
              <KeenIcon icon="notepad-edit" className="mr-0.5 h-4 w-4 cursor-pointer" /> Edit
            </DropdownMenuItem>
          )}
          {/* && payment_status !== 'paid' && invoice_status === 'pending' */}
          {canDeleteInvoice && (
            <DropdownMenuItem onClick={() => openModal(id)} className="text-red-600 cusror-pointer">
              <KeenIcon icon="trash" className="mr-0.5 h-4 w-4 cursor-pointer" /> Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }, [
    canApproveInvoice,
    canDeleteInvoice,
    canEditInvoice,
    contactData?.status,
    handleApprove,
    handleClone,
    handleEdit,
    handleSendInvoicetoContact,
    openModal,
  ]);
  const ajaxConfig = createDataTableConfig('/setup/invoices/data_table_list', filterRef, token, logout);


  const updateSelectableCells = useCallback((table) => {
    table.rows().every(function () {
      const rowNode = this.node();
      const data = this.data();
      const cell = rowNode?.querySelector('td.dt-row-select, td.dt-select-disabled');
      if (!cell) return;
      const payable = isPayable(data);
      const input = cell.querySelector('.row-select-checkbox');

      // Dropping `dt-row-select` is what actually blocks selection: it's the
      // select.selector DataTables matches clicks against.
      if (selectionModeRef.current === 'payment' && !payable) {
        if (this.selected()) this.deselect();
        cell.classList.remove('dt-row-select');
        cell.classList.add('dt-select-disabled');
        cell.title = 'Not selectable (unpaid status)';
        cell.style.pointerEvents = 'none';
        cell.style.opacity = '0.35';
        if (input) {
          input.checked = false;
          input.disabled = true;
        }
      } else {
        cell.classList.remove('dt-select-disabled');
        cell.classList.add('dt-row-select');
        cell.title = '';
        cell.style.pointerEvents = '';
        cell.style.opacity = '';
        if (input) input.disabled = false;
      }
    });
  }, [isPayable]);

  const options = useMemo(() => createDataTableOptions({
    order: [[14, 'desc']],
    select: {
      style: 'multi',
      selector: 'td.dt-row-select'
    },
    initComplete: function () {
      const api = this.api();
      setupHeaderCheckbox(api);
      api.on('select deselect draw', () => {
        syncHeaderCheckbox();
        syncRowCheckboxes();
      });
      api.on('page', () => {
        api.rows().deselect();
      });
      api.columns().every(function () {
        const col = this;
        const name = col.settings()[0].aoColumns[col.index()].name;
        if (name && !defaultVisibleColumns.includes(name)) {
          col.visible(false);
        }
      });
      syncHeaderCheckbox();
    },
    // Disable the checkbox cell for ineligible rows
    createdRow: function (row, data) {
      const eligible = isPayable(data);
      if (selectionModeRef.current === 'payment' && !eligible) {
        const cell = row.querySelector('td.dt-row-select');
        if (cell) {
          cell.classList.remove('dt-row-select');
          cell.classList.add('dt-select-disabled');
          cell.title = 'Not selectable (unpaid status)';
          cell.style.pointerEvents = 'none';
          cell.style.opacity = '0.35';
          const input = cell.querySelector('.row-select-checkbox');
          if (input) input.disabled = true;
        }
      }
    },
    drawCallback: function () {
      const api = this.api();
      const rows = api.rows({ page: 'current' }).data();

      // Matter availability on the current page
      let matterAvailable = true;
      // rows.each((row) => { if (isOpenMatter(row.matter)) matterAvailable = true; });
      // setIsMatterAvailable(matterAvailable);

      // Any payable rows on the current page?
      let payableExists = false;
      rows.each((row) => { if (isPayable(row)) payableExists = true; });
      setHasPayable(payableExists);
      updateSelectableCells(api);
      syncHeaderCheckbox();
      syncRowCheckboxes();

      if (selectionModeRef.current === 'payment') {
        const totalPayable = api
          .rows((idx, data) => isPayable(data))
          .count();
        const selectedPayable = api
          .rows({ selected: true })
          .data()
          .toArray()
          .filter(isPayable).length;
        setAllSelectedPayable(totalPayable > 0 && totalPayable === selectedPayable);
      }

      // Your React cell renderers (unchanged)
      rows.each((row) => {
        const container = document.getElementById(`action-cell-${row.id}`);
        if (container) {
          if (!container._reactRoot) container._reactRoot = createRoot(container);
          container._reactRoot.render(renderCustomActions(row));
        }
        const contactContainer = document.getElementById(`contact-cell-${row.id}`);
        if (contactContainer) {
          if (!contactContainer._reactRoot) contactContainer._reactRoot = createRoot(contactContainer);
          contactContainer._reactRoot.render(renderContactName(row));
        }
        const matterContainer = document.getElementById(`matter-cell-${row.id}`);
        if (matterContainer) {
          if (!matterContainer._reactRoot) matterContainer._reactRoot = createRoot(matterContainer);
          matterContainer._reactRoot.render(renderMatterName(row));
        }
        const numberContainer = document.getElementById(`number-cell-${row.id}`);
        if (numberContainer) {
          if (!numberContainer._reactRoot) numberContainer._reactRoot = createRoot(numberContainer);
          numberContainer._reactRoot.render(renderNumber(row));
        }
        const userContainer = document.getElementById(`user-cell-${row.id}`);
        if (userContainer) {
          if (!userContainer._reactRoot) userContainer._reactRoot = createRoot(userContainer);
          userContainer._reactRoot.render(renderMultiAvatars(row.approved_by));
        }
        const settlementContainer = document.getElementById(`settlement-cell-${row.id}`);
        if (settlementContainer) {
          if (!settlementContainer._reactRoot) {
            settlementContainer._reactRoot = createRoot(settlementContainer);
          }

          settlementContainer._reactRoot.render(
            <SettlementEditorModal
              row={row} refresh={refresh} fetchPaymentHistory={fetchPaymentHistory}
            />
            // <SettlementEditor row={row} refresh={refresh} />
          );
        }
      });
    },
  }), [
    fetchPaymentHistory,
    isPayable,
    refresh,
    renderContactName,
    renderCustomActions,
    renderMatterName,
    renderNumber,
    setupHeaderCheckbox,
    syncHeaderCheckbox,
    syncRowCheckboxes,
    updateSelectableCells,
  ]);

  useEffect(() => {
    if (!tableRef.current) return;
    const table = tableRef.current.dt();

    const updatePaymentSelectionState = () => {
      if (selectionModeRef.current !== 'payment') return;
      const eligibleRows = table
        .rows((idx, data) => isPayable(data))
        .count();
      const selectedEligibleRows = table
        .rows({ selected: true })
        .data()
        .toArray()
        .filter(isPayable).length;
      setAllSelectedPayable(eligibleRows > 0 && eligibleRows === selectedEligibleRows);
    };

    const enforcePayableSelection = (e, dt, type, indexes) => {
      if (selectionModeRef.current !== 'payment') return;
      if (!Array.isArray(indexes)) return;
      indexes.forEach((idx) => {
        const row = table.row(idx);
        if (!isPayable(row.data())) {
          row.deselect();
        }
      });
    };

    table.on("select", enforcePayableSelection);
    table.on("select deselect", updatePaymentSelectionState);
    return () => {
      table.off("select", enforcePayableSelection);
      table.off("select deselect", updatePaymentSelectionState);
    };
  }, [isPayable]);

  useEffect(() => {
    selectionModeRef.current = selectionMode;
    const table = tableRef.current?.dt?.();
    if (!table) return;
    table.rows().deselect();
    setAllSelectedPayable(false);
    updateSelectableCells(table);
    syncHeaderCheckbox();
  }, [selectionMode, syncHeaderCheckbox, updateSelectableCells]);


  const resetFilters = () => {
    const cleared = {
      approved_by: null,
      invoice_status: null,
      contact_id: contactID,
      matter_id: null,
      from_date: null,
      to_date: null,
      date_range: null,
    };

    setFilterData(cleared);
    filterRef.current = cleared;
    refresh()
  };

  const handleFilterChange = (key, val) => {
    setFilterData((prev) => {
      const updated = { ...prev, [key]: val };
      filterRef.current = updated;
      // //console.log('updated',updated)
      return updated;
    });
  };

  const handleToggleFilter = () => {
    setShowFilter(prev => !prev)
  }

  const fetchLookup = async (type) => {
    switch (type) {
      case 'users':
        const { data } = await userService.getAllUsers();
        return data.map((user) => ({ label: user.first_name + ' ' + user.last_name, value: user.id, email: user.email }));
      case 'contact':
        return await fetchContactLookup();
      case 'matter':
        const matters = await matterService.getAllMatters();
        return matters.data.map((matter) => ({ label: matter.name, value: matter.id }));

      default:
        return [];
    }
  };

  const handleCloseOfflinePaymentModal = () => {
    setPaymentRows([])
    setOfflinePaymentModal(false)
  }

  const clearSelection = () => {
    const table = tableRef.current?.dt?.();
    if (table) {
      table.rows().deselect();
    }
    setAllSelectedPayable(false);
  }

  const handleMakeOfflinePayment = async (values) => {
    setOfflinePaymentLoading(true)
    try {
      const payload = {
        model_id: contactID,
        model_type: 'contact',
        invoice_ids: paymentRows.map(inv => inv.id),
        settlement_price: values.settlement_price,
        payment_note: values.payment_note,
        is_settled: values.is_settled
      }
      // console.log('payload', payload);
      // return
      const { data, status } = await invoiceService.makeOfflineInvoicePayment(payload)
      if (status) {
        fetchPaymentHistory()
        clearSelection()
        setPaymentRows([])
        refresh()
        handleCloseOfflinePaymentModal()
        toast.success('Invoices Paid Successfully.', {
          description: 'Invoices have been paid successfully.',
        })
      }
    } catch (error) {
      console.error('Error', error)
      toast.error('Failed to Make invoice Payments.', {
        description: 'An error occurred while making invoice payments. Please try again later.',
      })
    } finally {
      setOfflinePaymentLoading(false)
    }
  }

  return (
    <>
      {/* Filters */}
      <FilterContainer
        show={showFilter}
        onApply={() => refresh()}
        onReset={resetFilters}
      >
        <SelectInput
          label="Approved By"
          type="users"
          value={filterData.approved_by}
          onChange={(val) => handleFilterChange('approved_by', val)}
          loadOptions={loadUserOptions}
          pageSize={20}
          customClass="sm:w-[32%] w-full"
        />
        <SelectInput
          label="Invoice Status"
          value={filterData.invoice_status}
          onChange={(val) => handleFilterChange('invoice_status', val)}
          staticOptions={STATUS_INVOICE_LISTING_OPTIONS.slice(0, -1)}
          customClass="sm:w-[32%] w-full"
        />

        <SelectInput
          label="Payment Status"
          value={filterData.payment_status}
          onChange={(val) => handleFilterChange('payment_status', val)}
          staticOptions={STATUS_INVOICE_LISTING_OPTIONS.slice(-3)}
          customClass="sm:w-[32%] w-full"
        />

        <DateInput
          customClass="sm:w-[32%] w-full"
          label="Created At - (From, To)"
          mode="range"
          value={filterData.date_range}
          onChange={(range) => {
            setFilterData(prev => ({
              ...prev,
              from_date: range?.from ?? null,
              to_date: range?.to ?? null,
              date_range: range,
            }));
          }}
        />
      </FilterContainer>
      {/* <div className={cn(
        ' slide-transition overflow-hidden',
        showFilter
          ? 'card mb-2 opacity-100 translate-y-0 sm:h-h-fit max-h-[815px]'
          : 'opacity-0 -translate-y-2 pointer-events-none max-h-0'
      )}>
        <div className='card-header card-title'>Filters</div>
        <div className='card-body'>
          <div className='flex flex-wrap items-center justify-start w-full gap-2'>
            <SelectInput
              label="Approved By"
              type="users"
              value={filterData.approved_by}
              onChange={(val) => handleFilterChange('approved_by', val)}
              loadOptions={loadUserOptions}
              pageSize={20}
              customClass="sm:w-[32%] w-full"
            />

            <SelectInput
                // isMulti={true}
                label="Contact"
                type="contact"
                value={filterData.contact}
                onChange={(val) => handleFilterChange('contact', val)}
                fetchLookup={fetchLookup}
                customClass="sm:w-[32%] w-full"
              />

              <SelectInput
                // isMulti={true}
                label="Matter"
                type="matter"
                value={filterData.matter}
                onChange={(val) => handleFilterChange('matter', val)}
                fetchLookup={fetchLookup}
                customClass="sm:w-[32%] w-full"
              />

            <SelectInput
              label="Invoice Status"
              value={filterData.invoice_status}
              onChange={(val) => handleFilterChange('invoice_status', val)}
              staticOptions={STATUS_INVOICE_LISTING_OPTIONS}
              customClass="sm:w-[32%] w-full"
            />

            <SelectInput
              label="Payment Status"
              value={filterData.payment_status}
              onChange={(val) => handleFilterChange('payment_status', val)}
              staticOptions={STATUS_INVOICE_LISTING_OPTIONS.slice(-2)}
              customClass="sm:w-[32%] w-full"
            />

            <DateInput
              customClass="sm:w-[32%] w-full"
              label="Created At - (From, To)"
              mode="range"
              value={filterData.date_range}
              onChange={(range) => {
                setFilterData(prev => ({
                  ...prev,
                  from_date: range?.from ?? null,
                  to_date: range?.to ?? null,
                  date_range: range,
                }));
              }}
            />
            <DateFilter
                value={filterData.created_at}
                onChange={(date) =>
                  setFilterData((prev) => ({
                    ...prev,
                    created_at: date,
                  }))
                }
              />
            <div className='flex flex-col w-max'>
              <div className='form-label text-sr-only'>.</div>
              <div className='flex items-center gap-1'>
                <button
                  className='btn btn-sm btn-primary'
                  type='submit'
                  onClick={(e) => {
                    e.preventDefault();
                    refresh();
                  }}>Apply</button>
                <button type='button' className='btn btn-sm btn-danger btn-outline' onClick={resetFilters}>Refresh</button>
              </div>
            </div>
          </div>
        </div>
      </div> */}
      <div className="min-w-full mb-4 card">
        <div className="card-header">
          <div className='flex items-center gap-1'>
            <ColumnToggleDropdown tableRef={tableRef} columns={columns} defaultVisibleColumns={defaultVisibleColumns} />
            {/* <h3 className="card-title">Invoices</h3> */}
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            {canEditInvoice && contactData?.status === 'active' && selectedRows.length > 0 && selectionMode === 'payment' && (
              <button
                className="text-xs btn btn-sm btn-success btn-outline"
                onClick={() => {
                  setPaymentRows(selectedRows);
                  setOfflinePaymentModal(true);
                }}
              //console.log('Selected Rows:', selectedRows)
              >
                <BadgeDollarSign className='size-4' />
                Settlement
              </button>
            )}

            {/* {canEditInvoice && contactData?.status === 'active' && hasPayable && selectionMode === 'payment' && (
              <button
                className="text-xs btn btn-sm btn-light"
                onClick={() => {
                  const table = tableRef.current.dt();

                  if (allSelectedPayable) {
                    table.rows().deselect();
                    setAllSelectedPayable(false);
                  } else {
                    // isOpenMatter(data.matter) &&
                    table.rows((idx, data) =>
                    (data.invoice_status === 'approved' ||
                      (data.invoice_status === 'billed' && data.payment_status === 'pending'))
                    ).select();
                    setAllSelectedPayable(true);
                  }
                }}
              >
                <MousePointerClick className="size-4" />
                {allSelectedPayable ? "Unselect All" : "Select All"}
              </button>
            )} */}

            {canDeleteInvoice && selectionMode === 'delete' && (
              <BulkDeleteActions
                canDelete={canDeleteInvoice}
                selectedCount={selectedRows.length}
                bulkDeleteLoading={bulkDeleteLoading}
                allSelected={allSelected}
                onOpenConfirm={openBulkDelete}
                onToggleSelectPage={toggleSelectPage}
                confirmOpen={bulkDeleteOpen}
                onCloseConfirm={closeBulkDelete}
                onConfirmDelete={confirmBulkDelete}
                confirmMessage={`
                  You are about to delete ${selectedRows.length} invoice(s).
                  You can restore it from the Recycle Bin within 30 days.
                `}
              />
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-xs btn btn-sm btn-light">
                  Bulk Selection: {selectionMode === 'payment' ? 'Settlement' : 'Delete'}
                  <ChevronDown className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => setSelectionMode('payment')}>
                  Settlement
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectionMode('delete')}>
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button className='text-xs btn btn-sm btn-primary btn-outline' onClick={handleToggleFilter}>
              <KeenIcon icon="filter" className="w-fit btn-icon" />
              {showFilter ? 'Hide Advanced Filters' : 'Advanced Filters'}
            </button>

            {canEditInvoice && contactData?.status === 'active' && (
              <Link
                to={`/billing/invoice/create?contactId=${contactID}&matterId=${matterID}`}
                // state={{ from: `/contacts/${contactID}`, contactId: contactID, matterId: matterID }}
                state={{ from: `/contacts/${contactID}?tab=Invoices`, contactId: contactID, matterId: matterID }}
                className='text-xs btn btn-sm btn-light'>
                <KeenIcon icon="plus" className="" />
                Add New
              </Link>
            )}
            {/* <Can I="handle" a="invoice" ability={ability}>
            </Can> */}
          </div>
        </div>


        <DataTable
          id="invoice-contact-table"
          className='invoice-contact-table table-with-checkbox'
          ajax={ajaxConfig}
          columns={columns}
          options={options}
          ref={tableRef}
        />

        <ConfirmationModal
          show={isOpen}
          onClose={closeModal}
          onConfirm={handleDelete}
          message="Are you sure you want to delete this Invoice? You can restore it from the Recycle Bin within 30 days."
          title="Are You Sure?"
        />

        <PaymentModal
          referesh={refresh}
          open={offlinePaymentModal}
          onConfirm={handleMakeOfflinePayment}
          onClose={handleCloseOfflinePaymentModal}
          invoiceData={paymentRows}
          loading={offlinePaymentLoading}
        />
      </div>
    </>
  )
}

export { InvoiceContactTab }
